import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { IconArrowLeft } from '@tabler/icons-react';

import AccordionItem from './Accordion';
import HeaderResult from '@components/Result/HeaderResult';
import InputPhoneNumber from '../InputPhoneNumber';
import Spinner from '@components/Spinner';
import { SettingsProps, DataDetailCheckout } from '@pages/types';

interface PhoneNumberProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  onConfirm: (phoneNumber: string) => void;
  onBindAccount?: (phoneNumber: string) => void;
  onGoBack: () => void;
  isLoading?: boolean;
  isDanaMethod?: boolean;
}

const phoneSchema = Yup.object().shape({
  phone_number: Yup.string()
    .test(
      'valid-phone-number',
      'Masukkan nomor telepon yang valid (format Indonesia)',
      (value) => {
        if (!value || value === '+62') return false;
        return /^(?:\+62|62|0)[2-9][0-9]{7,12}$/.test(value);
      }
    )
    .required('Nomor telepon wajib diisi'),
});

const PhoneNumber: React.FC<PhoneNumberProps> = ({
  settings,
  onConfirm,
  onBindAccount,
  onGoBack,
  isLoading = false,
  isDanaMethod = false,
}) => {
  const [accordionOpen, setAccordionOpen] = useState(true);
  const [submitAction, setSubmitAction] = useState<'continue' | 'bind'>(
    'continue'
  );

  const phonenumber_info = isDanaMethod
    ? [
        'Masukkan nomor telepon DANA yang aktif dan terdaftar pada akun Anda.',
        "Pilih 'Bind Account' jika ingin menghubungkan akun DANA terlebih dahulu, atau pilih 'Continue to APP' untuk langsung melanjutkan pembayaran.",
        'Selesaikan otorisasi di aplikasi DANA, lalu kembali ke halaman checkout jika dibutuhkan untuk melanjutkan proses pembayaran.',
      ]
    : [
        'Masukkan nomor telepon yang terdaftar pada layanan pembayaran Anda.',
        "Klik 'Continue to APP' untuk mengirim permintaan pembayaran Anda.",
        'Anda akan menerima notifikasi konfirmasi setelah pembayaran berhasil.',
      ];

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onGoBack}
        disabled={isLoading}
        className="flex w-max items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <IconArrowLeft className="h-5 w-5" />
        <span className="font-medium">Back to Payment Methods</span>
      </button>

      <HeaderResult
        title="Phone Number"
        subtitle="Enter your phone number to complete the payment"
      />

      <Formik
        initialValues={{ phone_number: '+62' }}
        validationSchema={phoneSchema}
        onSubmit={(values) => {
          if (isDanaMethod && submitAction === 'bind') {
            onBindAccount?.(values.phone_number);
            return;
          }

          const formatted =
            values.phone_number === '+62'
              ? ''
              : values.phone_number.replace(/^\+/, '');
          if (formatted) onConfirm(formatted);
        }}
      >
        {({ handleSubmit }) => (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-4 shadow-sm">
              <InputPhoneNumber
                name="phone_number"
                label="Phone Number"
                required
              />

              <div className="flex flex-row items-center justify-center gap-4 bg-white px-4 py-3 md:relative md:px-0">
                {isDanaMethod ? (
                  <>
                    {/* <button
                      type="button"
                      onClick={() => {
                        setSubmitAction('bind');
                        handleSubmit();
                      }}
                      disabled={isLoading}
                      className="flex w-full items-center justify-center rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 font-semibold text-gray-800 transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isLoading ? <Spinner /> : 'Bind Account'}
                    </button> */}
                    <button
                      type="button"
                      onClick={() => {
                        setSubmitAction('continue');
                        handleSubmit();
                      }}
                      disabled={isLoading}
                      className="flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{
                        backgroundColor: settings.button_color
                          ? settings.button_color
                          : '#5D87FF',
                        color: settings.button_text_color
                          ? settings.button_text_color
                          : '#FFFFFF',
                      }}
                    >
                      {isLoading ? <Spinner /> : 'Continue to APP'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitAction('continue');
                      handleSubmit();
                    }}
                    disabled={isLoading}
                    className="flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      backgroundColor: settings.button_color
                        ? settings.button_color
                        : '#5D87FF',
                      color: settings.button_text_color
                        ? settings.button_text_color
                        : '#FFFFFF',
                    }}
                  >
                    {isLoading ? <Spinner /> : 'Continue to APP'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Formik>

      <div>
        <div className="flex flex-col gap-1">
          <AccordionItem
            title="Payment Instructions"
            content={
              <div className="flex flex-col gap-4">
                {phonenumber_info.map((instruction, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-semibold">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{instruction}</p>
                    </div>
                  </div>
                ))}
              </div>
            }
            isOpen={accordionOpen}
            onToggle={() => setAccordionOpen(!accordionOpen)}
          />
        </div>
      </div>
    </div>
  );
};

export default PhoneNumber;
